import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { GradientTextAction } from 'src/common/GradientText';
import { MOTION_TRANSITION } from 'src/config';
import { IntelNote } from '../types';

const Line = styled(Poppins)`
  display: block;
  line-height: 1.7;
  /* The note may carry \n\n paragraph breaks — preserve them instead of collapsing to a space. */
  white-space: pre-wrap;
`;

export const QuantNote: React.FC<{ note: IntelNote }> = ({ note }) => {
  const [open, setOpen] = useState(false);
  // Only offer "See more" when there's actually structured content to reveal — the live wire note is a
  // flat string, so without this the expander opens onto an empty "Supporting Research" header.
  const hasMore =
    note.supportingResearch.length > 0 || (note.consider?.length ?? 0) > 0 || !!note.manualUpdateStatement;

  return (
    <div>
      <Line px={16} color="cflowerBlue">
        {note.basisOfEstimate}
        {hasMore && (
          <>
            {' '}
            <GradientTextAction px={16} weight={500} onClick={() => setOpen((o) => !o)}>
              {open ? 'See less' : 'See more'}
            </GradientTextAction>
          </>
        )}
      </Line>

      {hasMore && (
      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0 }}
        transition={MOTION_TRANSITION}
        style={{ overflow: 'hidden' }}
      >
        <Spacer $px={16} />
        <Poppins px={14} weight={600} color="prussianBlue">
          Quantification Supporting Research
        </Poppins>
        <Spacer $px={8} />
        {note.supportingResearch.map((r, i) => (
          <Line key={r.sourceTitle} px={14} color="cflowerBlue">
            {i + 1}. {r.sourceTitle}
            {r.year ? ` (${r.year})` : ''} found that {r.statistic}
            {r.confidence != null ? ` Confidence ${r.confidence}%` : ''}
          </Line>
        ))}

        {note.consider && note.consider.length > 0 && (
          <>
            <Spacer $px={12} />
            <Poppins px={14} weight={600} color="prussianBlue">
              Consider
            </Poppins>
            <Spacer $px={8} />
            {note.consider.map((c) => (
              <Line key={c} px={14} color="cflowerBlue">
                - {c}
              </Line>
            ))}
          </>
        )}

        {note.manualUpdateStatement && (
          <>
            <Spacer $px={12} />
            <Line px={14} color="cflowerBlue" css="font-style: italic;">
              {note.manualUpdateStatement}
            </Line>
          </>
        )}
      </motion.div>
      )}
    </div>
  );
};
